import { motion } from 'framer-motion'
import { CREATE_MODES } from '../../utils/ticketCreateFlows'

/**
 * Pantalla inicial de un flujo con varios modos (p. ej. /nuevo-ticket o
 * /reportar-incidente): la persona elige cómo continuar.
 *
 * @component
 * @param {Object} props
 * @param {(key: string) => void} props.onSelect - Recibe la clave del modo.
 * @param {string|null} [props.lastUsed] - Último modo usado (se destaca).
 * @param {Array<Object>} [props.modes=CREATE_MODES] - Opciones (key, title,
 *   icon, color, description, bullets, badge?).
 * @param {string} [props.title]
 * @param {string} [props.subtitle]
 * @returns {JSX.Element}
 */
const CreateModeSelector = ({
  onSelect,
  lastUsed,
  modes = CREATE_MODES,
  title = '¿Cómo quiere crear su ticket?',
  subtitle = 'Elija la opción con la que se sienta más cómodo. Puede cambiarla en cualquier momento.',
}) => (
  <div className="cms">
    <h5 className="cms-title">{title}</h5>
    <p className="cms-sub">{subtitle}</p>

    <div className="cms-grid">
      {modes.map((mode, i) => (
        <motion.button
          key={mode.key}
          type="button"
          className="cms-card"
          style={{ '--mode-color': mode.color }}
          onClick={() => onSelect(mode.key)}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.06 }}
          whileHover={{ y: -3 }}
        >
          <div className="d-flex justify-content-between align-items-start">
            <span className="cms-icon">
              <i className={`fa-solid ${mode.icon}`} />
            </span>
            {lastUsed === mode.key ? (
              <span className="cms-badge is-last">Último usado</span>
            ) : (
              mode.badge && <span className="cms-badge">{mode.badge}</span>
            )}
          </div>
          <span className="cms-card-title">{mode.title}</span>
          <span className="cms-card-desc">{mode.description}</span>
          <ul className="cms-bullets">
            {mode.bullets.map((b) => (
              <li key={b}>
                <i className="fa-solid fa-check" />
                {b}
              </li>
            ))}
          </ul>
          <span className="cms-cta">
            Empezar <i className="fa-solid fa-arrow-right ms-1" />
          </span>
        </motion.button>
      ))}
    </div>

    <style>{`
      .cms-title { font-weight: 700; color: var(--gm-text); margin-bottom: 4px; }
      .cms-sub { font-size: 0.88rem; color: var(--gm-text-muted); margin-bottom: 18px; }
      .cms-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 14px; }
      .cms-card {
        display: flex; flex-direction: column; gap: 8px; padding: 20px; text-align: left;
        border: 1px solid var(--gm-border); border-radius: 16px; background: var(--gm-surface);
        transition: border-color 0.2s ease, box-shadow 0.2s ease;
      }
      .cms-card:hover, .cms-card:focus-visible {
        border-color: var(--mode-color); box-shadow: 0 10px 24px -12px var(--mode-color); outline: none;
      }
      .cms-icon {
        width: 46px; height: 46px; border-radius: 14px; display: flex; align-items: center;
        justify-content: center; font-size: 1.2rem; color: var(--mode-color);
        background: color-mix(in srgb, var(--mode-color) 14%, transparent);
      }
      /* El color de cada modo (turquesa, azul) se lee bien sobre fondo oscuro,
         pero como texto sobre blanco cae a 2.5:1. En claro se oscurece; en
         oscuro se deja el color, que es donde ya funcionaba. */
      .cms-badge, .cms-cta { color: color-mix(in srgb, var(--mode-color) 62%, #000); }
      :root[data-theme='dark'] .cms-badge,
      :root[data-theme='dark'] .cms-cta { color: var(--mode-color); }
      .cms-badge {
        font-size: 0.68rem; font-weight: 700; padding: 3px 10px; border-radius: 99px;
        background: color-mix(in srgb, var(--mode-color) 14%, transparent);
      }
      .cms-badge.is-last { color: var(--gm-text-muted); background: var(--gm-surface-2); }
      .cms-card-title { font-size: 1.05rem; font-weight: 700; color: var(--gm-text); margin-top: 6px; }
      .cms-card-desc { font-size: 0.85rem; color: var(--gm-text-muted); }
      .cms-bullets { list-style: none; padding: 0; margin: 4px 0 0; flex: 1; }
      .cms-bullets li { font-size: 0.8rem; color: var(--gm-text); margin-bottom: 4px; display: flex; gap: 8px; align-items: baseline; }
      .cms-bullets i { color: var(--mode-color); font-size: 0.7rem; }
      .cms-cta { font-size: 0.85rem; font-weight: 600; margin-top: 6px; }
    `}</style>
  </div>
)

export default CreateModeSelector
